import { useEffect, useMemo, useState } from "react";
import { MenuItemProps, MoreScreenValues } from "./types";
import { PriceModes } from "@components/molecules/PriceModeCard/types";
import { useUserInfo } from "@redux/slices/userInfoSlice";
import Routes from "@navigations/Routes";
import { VDBIcons } from "@utils/VDBIcons";
import { setLoginModalType } from "@redux/slices/appStateSlice";
import { ButtonType } from "@components/atoms/Button/types";
import { TeamMember } from "@src/types/UserList";
import { messageType } from "@components/molecules/PopUp/types";
import { navigate } from "@navigations/utils/RootNavigation";
import { EventName, EventType, ViewType, ViewTypeEnum } from "@utils/analytics";

export const defaultSections = [
  {
    title: "General",
    name: "general",
    data: [
      {
        id: "price_mode",
        itemLeftText: "Price Mode",
        action: "price_mode",
      },
      {
        id: "default_search_group",
        itemLeftText: "Default Search Group",
        action: "default_search_group",
      },
      {
        id: "biometrics",
        itemLeftText: "Authorization",
        action: "biometrics",
      },
      {
        id: "login",
        itemLeftText: "login",
        action: "login",
      },
      {
        id: "register",
        itemLeftText: "register",
        action: "register",
      },
    ],
  },
  {
    title: "Language & Currency",
    name: "languageCurrency",
    data: [
      {
        id: "languages",
        itemLeftText: "Language",
        action: "languages",
      },
      {
        id: "currency",
        itemLeftText: "Currency",
        action: "currency",
      },
    ],
  },
  {
    title: "Integrations",
    name: "integrations",
    data: [
      {
        id: "jbt_id",
        name: "jbt_id",
        itemLeftText: "Connect JBT Account",
        leftIcon: "",
        leftIconTheme: "",
        action: "jbt_connect",
      },
    ],
  },
  {
    title: "Guides & Tutorials",
    name: "guides_and_tutorials",
    data: [
      {
        id: "post_onboarding",
        itemLeftText: "Revisit OnBoarding",
        leftIcon: "",
        leftIconTheme: "",
        action: "post_onboarding",
      },
      {
        id: "coach_overlay",
        itemLeftText: "Coach Overlays",
        leftIcon: "",
        leftIconTheme: "",
        action: "coach_overlay",
      },
    ],
  },
  {
    title: "Other",
    name: "other",
    data: [
      {
        id: "terms_of_use",
        itemLeftText: "Terms of Use",
        action: "terms_of_use",
      },
      {
        id: "contact_us",
        itemLeftText: "Contact Us",
        action: "contact_us",
      },
      {
        id: "delete_account",
        itemLeftText: "Delete Account",
        action: "delete_account",
      },
      {
        id: "logout",
        itemLeftText: "Sign Out",
        leftIcon: VDBIcons.LOGOUT,
        leftIconTheme: "core-04",
        action: "logout",
      },
    ],
  },
];
export const useMoreScreenValues = (biometricType: string | null) => {
  const userInfo = useUserInfo();
  const [selectedValues, setSelectedValues] = useState<MoreScreenValues>({
    price_mode: PriceModes.WHITE,
    price_markup: 0,
    jbt_id: "",
  });

  useEffect(() => {
    const valuesObj = {
      price_mode: userInfo.user.price_mode,
      price_markup: userInfo.user.markup_amount + userInfo.user.markup_unit,
      jbt_id: userInfo.user?.jbt_detail?.jbt_id || null,
    };
    setSelectedValues(valuesObj);
  }, [userInfo.user, biometricType]);

  return selectedValues;
};
export const checkItemConditions = (
  item: any,
  selectedValues: any,
  userInfo: any,
  biometricType: any,
  isGuest: boolean,
  settings?: any,
  theme?: any
) => {
  let returnValue = true;
  switch (item.name) {
    case "languages":
      returnValue = settings?.organization?.enable_language_translation;
      break;
    case "jbt_id":
      returnValue =
        selectedValues.price_mode === PriceModes.WHITE &&
        !settings?.disable_jbt;
      break;
    case "price_mode":
      returnValue =
        (!userInfo.forceGreenPriceProfile() &&
          userInfo.priceProfileVisibility() &&
          !userInfo.isPriceModeLock()) ||
        (userInfo.isPriceModeLock() && userInfo.userCanSetMarkupInOrangeMode());
      break;
    case "biometrics":
      returnValue = !!biometricType;
      break;
    case "currency":
      returnValue = userInfo.currencyConversion() && !userInfo.lockCurrency();
      break;
    case "default_search_group":
      returnValue =
        userInfo.user?.can_choose_search_gorup && userInfo.user?.search_pref;
      break;
    case "login":
      returnValue = isGuest;
      break;
    case "register":
      returnValue = isGuest;
      break;
    case "account":
      returnValue = userInfo.user?.is_admin_true && theme.isLargeScreen;
      break;
    case "account_overview":
      returnValue = userInfo.user?.is_admin_true && !theme.isLargeScreen;
      break;
    case "company_details":
      returnValue = userInfo.user?.is_admin_true && !theme.isLargeScreen;
      break;
    case "user_seats":
      returnValue = userInfo.user?.is_admin_true && !theme.isLargeScreen;
      break;
    default:
      returnValue = true;
      break;
  }

  return returnValue;
};

export const actionFunctions = ({
  item,
  dispatch,
  navigation,
  onShowContactUsModal,
  onShowJBTModal,
  onShowPostOnboarding,
  onShowSignOutPopup,
  onShowPriceModeModal,
  onShowTermsAndConditionsModal,
  onShowDeleteAccountModal,
  onShowDefaultSearchGroupsModal,
  onShowCoachOverlayModal,
  onShowPagesModal,
  theme,
  trackButtonEvent,
}: {
  item: any;
  dispatch?: any;
  navigation: any;
  onShowSignOutPopup: () => void;
  onShowContactUsModal: () => void;
  onShowJBTModal: () => void;
  onShowPostOnboarding: () => void;
  onShowPriceModeModal?: () => void;
  onShowTermsAndConditionsModal?: () => void;
  onShowDeleteAccountModal?: () => void;
  onShowDefaultSearchGroupsModal?: () => void;
  onlogout?: () => void;
  onShowCoachOverlayModal?: () => void;
  onShowPagesModal?: () => void;
  theme?: any;
  trackButtonEvent?: any;
}) => {
  const action = item.name;
  switch (action) {
    case "logout":
      onShowSignOutPopup();
      break;
    case "contact_us":
      onShowContactUsModal();
      break;
    case "delete_account":
      if (onShowDeleteAccountModal instanceof Function) {
        onShowDeleteAccountModal();
      } else if (navigation) {
        navigate(Routes.DeleteAccount);
      }
      break;
    case "languages":
      if (navigation) {
        navigate(Routes.Languages);
      }
      break;
    case "currency":
      if (navigation) {
        navigate(Routes.Currency);
      }
      break;
    case "price_mode":
      if (onShowPriceModeModal instanceof Function) {
        onShowPriceModeModal();
      } else if (navigation) {
        navigate(Routes.ChangePriceMode);
      }
      break;
    case "terms_of_use":
      if (onShowTermsAndConditionsModal instanceof Function) {
        onShowTermsAndConditionsModal();
      } else if (navigation) {
        navigate(Routes.TermsOfUse);
      }
      break;
    case "post_onboarding":
      onShowPostOnboarding();
      break;
    case "coach_overlay":
      if (onShowCoachOverlayModal instanceof Function) {
        onShowCoachOverlayModal();
      } else if (navigation) {
        navigate(Routes.CoachOverlayScreen);
      }
      break;
    case "biometrics":
      if (navigation) {
        navigate(Routes.BiometricAuthorization);
      }
      break;
    case "jbt_id":
      onShowJBTModal();
      break;
    case "login":
      dispatch(setLoginModalType("default"));
      break;
    case "register":
      if (navigation) {
        navigate(Routes.GlobalNavigator, {
          headerShown: "true",
          screen: Routes.Register,
          params: {
            fromGlobal: true,
          },
        });
      }
      break;
    case "default_search_group":
      if (onShowDefaultSearchGroupsModal instanceof Function) {
        onShowDefaultSearchGroupsModal();
      } else if (navigation) {
        navigate(Routes.DefaultSearchGroup);
      }
      break;
    case "pages":
      if (onShowPagesModal instanceof Function) {
        onShowPagesModal();
      } else if (navigation) {
        navigate(Routes.PagesScreen, item.params);
      }
      break;
    case "account":
      if (navigation) {
        if (theme.isLargeScreen) {
          navigate(Routes.ExploreNavigator, {
            screen: Routes.AccountOverviewScreen,
          });
        } else {
          navigate(Routes.AccountOverviewUsersSeats);
        }
      }
      break;
    case "account_overview":
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.ACCOUNT_OVERVIEW_TAB_CLICK,
        { component_type: ViewType.SCREEN_VIEW },
        ViewTypeEnum?.ACCOUNT_OVERVIEW_SCREEN_VIEW
      );
      navigate(Routes.AccountOverviewOverview);
      break;
    case "company_details":
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.COMPANY_DETAILS_TAB_CLICK,
        { component_type: ViewType.SCREEN_VIEW },
        ViewTypeEnum?.COMPANY_DETAILS_SCREEN_VIEW
      );
      navigate(Routes.AccountOverviewCompanyDetails);
      break;
    case "user_seats":
      trackButtonEvent(
        EventName.BUTTON_CLICK,
        EventType.USER_SEATS_TAB_CLICK,
        { component_type: ViewType.SCREEN_VIEW },
        ViewTypeEnum?.USER_SEATS_SCREEN_VIEW
      );
      navigate(Routes.AccountOverviewUsersSeats);
      break;
    default:
      break;
  }
};

export const handleItemSelect = (
  item: MenuItemProps,
  dispatch?: any,
  setSelectedItemId?: any,
  setAllowPostOnboadingNavigation?: any,
  onItemClick?: any,
  showAsPopover?: any,
  navigation?: any,
  showModal?: any,
  theme?: any,
  trackButtonEvent?: any
) => {
  setSelectedItemId(item.id);
  if (onItemClick instanceof Function) {
    onItemClick(item);
  } else {
    actionFunctions({
      item,
      dispatch,
      navigation,
      onShowSignOutPopup: () => showModal("sign-out-popup"),
      onShowContactUsModal: () => showModal("contact-us"),
      onShowJBTModal: () => showModal("jbt"),
      onShowPostOnboarding: () => setAllowPostOnboadingNavigation(true),
      onShowPriceModeModal: showAsPopover
        ? () => showModal("price-mode")
        : undefined,
      onShowTermsAndConditionsModal: showAsPopover
        ? () => showModal("terms-and-conditions")
        : undefined,
      onShowDeleteAccountModal: showAsPopover
        ? () => showModal("delete-account")
        : undefined,
      onShowDefaultSearchGroupsModal: showAsPopover
        ? () => showModal("default-search-group")
        : undefined,
      onShowPagesModal: showAsPopover
        ? () => showModal("pages", item.params)
        : undefined,
      theme,
      trackButtonEvent,
    });
  }
};
export const useMergeSections = (customSections, initialSections) => {
  const mergedSections = useMemo(() => {
    const sections = customSections?.map((customSection) => {
      const matchingDefaultSection = initialSections?.find(
        (defaultSection) => defaultSection.name === customSection.title
      );

      return {
        ...matchingDefaultSection,
        ...customSection,
        data: customSection.data
          ?.map((customItem) => {
            const matchingDefaultItem = matchingDefaultSection?.data?.find(
              (defaultItem) => defaultItem.id === customItem.id
            );

            return {
              ...matchingDefaultItem,
              ...customItem,
              itemLeftText:
                customItem.itemLeftText || matchingDefaultItem?.itemLeftText,
              leftIcon: customItem.leftIcon || matchingDefaultItem?.leftIcon,
              leftIconTheme:
                customItem.leftIconTheme || matchingDefaultItem?.leftIconTheme,
              order: customItem.order || matchingDefaultItem?.order || 0,
              price_modes:
                customItem.price_modes ||
                matchingDefaultItem?.price_modes ||
                [],
            };
          })
          .sort((a, b) => a.order - b.order),
      };
    });

    const remainingDefaultSections =
      initialSections?.filter(
        (defaultSection) =>
          !customSections.some(
            (customSection) => customSection.title === defaultSection.name
          )
      ) || [];

    return [...sections, ...remainingDefaultSections]?.sort(
      (a, b) => a.order - b.order
    );
  }, [customSections, initialSections]);

  return mergedSections;
};

export enum ThreeDotOptionsEnum {
  MAKE_ADMIN = "make_admin",
  MAKE_MEMBER = "make_member",
  REMOVE_USER = "remove_user",
  ACTIVATE = "activate_user",
  LIMIT_ACCESS = "limit_access",
}

export const threeDotOptions = [
  {
    name: ThreeDotOptionsEnum.MAKE_ADMIN,
    label: "Make Admin",
  },

  {
    name: ThreeDotOptionsEnum.MAKE_MEMBER,
    label: "Make Member",
  },
  {
    name: ThreeDotOptionsEnum.ACTIVATE,
    label: "Grant Full Access",
  },
  {
    name: ThreeDotOptionsEnum.LIMIT_ACCESS,
    label: "Limit Access",
  },
  {
    name: ThreeDotOptionsEnum.REMOVE_USER,
    label: "Remove User",
  },
];

export const menu = [
  // {
  //   id: "overview",
  //   label: "Overview",
  //   route: "account-overview-overview",
  //   cards: [
  //     {
  //       id: "current_plan",
  //       title: "Current Plan",
  //       price_modes: [0, 1, 2],
  //       fields: [
  //         {
  //           id: "plan",
  //           label: "Plan",
  //           value_key: "plan_name",
  //           show: true,
  //         },
  //         {
  //           id: "ends_in",
  //           label: "Ends in",
  //           value_key: "plan_ends_in",
  //           show: true,
  //         },
  //       ],
  //       buttons: [
  //         {
  //           id: "manage_plan",
  //           label: "Manage Plan",
  //           action: "manage_plan",
  //           price_modes: [0, 1, 2],
  //         },
  //       ],
  //     },
  //     {
  //       id: "team_seats",
  //       title: "Company Users",
  //       price_modes: [0, 1, 2],
  //       fields: [
  //         {
  //           id: "free",
  //           label: "Total Users",
  //           value_key: "total_active_users",
  //           show: true,
  //         },
  //         {
  //           id: "used",
  //           label: "Currently Active",
  //           value_key: "used_seats",
  //           show: true,
  //         },
  //         {
  //           id: "total",
  //           label: "Purchased Users",
  //           value_key: "paid_users",
  //           show: true,
  //         },
  //         {
  //           id: "available",
  //           label: "Unassigned Users",
  //           value_key: "remaining_seats",
  //           show: true,
  //         },
  //       ],
  //       buttons: [
  //         {
  //           id: "manage_seats",
  //           label: "Manage Users",
  //           action: "manage_seats",
  //           price_modes: [0, 1, 2],
  //         },
  //       ],
  //     },
  //   ],
  // },
  {
    id: "users_seats",
    label: "Access & Users",
    route: "account-overview-users-seats",
  },
  {
    id: "company_details",
    label: "Company Details",
    route: "account-overview-company-details",
  },
];

export const DummyData = {
  plan_name: "Trial",
  plan_ends_in: "13 days (Oct 3, 2025)",
  seats_total: 10,
  seats_free: 1,
  seats_used: 5,
  seats_available: 5,
};

export const companyDetailsCustomization = (
  t: (key: string, defaultText: string) => string
) => {
  return [
    {
      key: "location_name",
      label: t("account.companyDetails.card.companyName.text", "Company Name"),
      order: 1,
      priceMode: [0, 1, 2],
    },
    {
      key: "website",
      label: t("account.companyDetails.card.website.text", "Company Website"),
      order: 2,
      priceMode: [0, 1, 2],
    },
    {
      key: "mobile_phone",
      label: t(
        "account.companyDetails.card.mobilePhone.text",
        "Mobile/Phone number"
      ),
      order: 4,
      priceMode: [0, 1, 2],
    },
  ];
};

export const companyAddressCustomization = (
  t: (key: string, defaultText: string) => string
) => {
  return [
    {
      key: "street_address",
      label: t("account.companyDetails.card.streetAddress.text", "Street"),
      order: 1,
      priceMode: [0, 1, 2],
    },
    {
      key: "city",
      label: t("account.companyDetails.card.city.text", "City"),
      order: 2,
      priceMode: [0, 1, 2],
    },
    {
      key: "state",
      label: t("account.companyDetails.card.state.text", "State"),
      order: 3,
      priceMode: [0, 1, 2],
    },
    {
      key: "zip",
      label: t("account.companyDetails.card.zip.text", "Postal/Zip"),
      order: 4,
      priceMode: [0, 1, 2],
    },
    {
      key: "country",
      label: t("account.companyDetails.card.country.text", "Country"),
      order: 5,
      priceMode: [0, 1, 2],
    },
  ];
};

export const usersSeatsHeaderCustomization = (
  t: (key: string, defaultText: string) => string
) => {
  return {
    title: t("account.userList.card.title.text", "Company Users"),
    price_modes: [0, 1, 2],
    fields: [
      {
        id: "free",
        label: t(
          "account.userList.card.fields.totalActiveUsers.text",
          "Total Users"
        ),
        value_key: "total_active_users",
        show: true,
      },
      {
        id: "total",
        label: t("account.userList.card.fields.total.text", "Purchased Users"),
        value_key: "paid_users",
        show: true,
      },
      {
        id: "used",
        label: t("account.userList.card.fields.used.text", "Currently Active"),
        value_key: "used_seats",
        show: true,
      },
      {
        id: "available",
        label: t(
          "account.userList.card.fields.available.text",
          "Unassigned Users"
        ),
        value_key: "remaining_seats",
        show: true,
      },
    ],
    buttons: [
      {
        id: "purchase_seats",
        label: t(
          "account.userList.card.buttons.purchaseUsers.text",
          "Purchase Users"
        ),
        action: "purchase_seats",
        price_modes: [0, 1, 2],
        type: ButtonType.secondary,
      },
      {
        id: "invite_users",
        label: t("account.userList.card.buttons.inviteUsers.text", "Add Users"),
        action: "invite_users",
        icon: VDBIcons.ADD_CIRCLE,
        iconColor: "primary-cont",
        price_modes: [0, 1, 2],
        type: ButtonType.primary,
      },
    ],
  };
};

export type UsersSeatsColumn = {
  key: "name_email" | "role" | "status" | "last_active" | "actions";
  label: string;
  order: number;
  priceMode: number[];
};

export const usersSeatsListCustomization = (
  t: (key: string, defaultText: string) => string
): UsersSeatsColumn[] => {
  return [
    {
      key: "name_email",
      label: t("account.userList.header.nameEmail.text", "Name & Email"),
      order: 1,
      priceMode: [0, 1, 2],
    },
    {
      key: "role",
      label: t("account.userList.header.role.text", "Role"),
      order: 2,
      priceMode: [0, 1, 2],
    },
    {
      key: "status",
      label: t("account.userList.header.status.text", "Status"),
      order: 3,
      priceMode: [0, 1, 2],
    },
    {
      key: "last_active",
      label: t("account.userList.header.lastActive.text", "Last Active"),
      order: 4,
      priceMode: [0, 1, 2],
    },
    {
      key: "actions",
      label: t("account.userList.header.actions.text", "Actions"),
      order: 5,
      priceMode: [0, 1, 2],
    },
  ];
};

export const getDaysLeft = (timestamp: string) => {
  const targetDate = new Date(timestamp); // expiry in UTC
  const now = new Date(); // current time in UTC

  if (isNaN(targetDate)) return "Invalid date";

  // Difference in milliseconds
  const diffMs = targetDate.getTime() - now.getTime();

  // Convert to days (always round *up* to include partial days)
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];
  const formattedDate = `${
    monthNames[targetDate.getUTCMonth()]
  } ${targetDate.getUTCDate()}, ${targetDate.getUTCFullYear()}`;

  return {
    daysLeftLabel: `${diffDays} Day${
      diffDays !== 1 ? "s" : ""
    } (${formattedDate})`,
    daysLeft: diffDays,
    daysLeftNoDate: `${diffDays} Day${diffDays !== 1 ? "s" : ""}`,
    expiryDate: formattedDate,
  };
};

export const userStatus = (user: TeamMember) => {
  if (
    user.status === "activated" &&
    user?.paid_plan_user === true &&
    user?.confirmed
  ) {
    return "activated";
  } else if (
    user.status === "activated" &&
    user?.paid_plan_user === false &&
    user?.confirmed
  ) {
    return "limited_access";
  } else if (user.status === "Invited" && !user?.confirmed) {
    return "invited";
  } else {
    return "invited";
  }
};

export const inviteUserFields = [
  {
    name: "first_name",
    labelKey: "inviteUser.input.firstName.label.text",
    placeholderKey: "inviteUser.input.firstName.placeholder.text",
    required: true,
  },
  {
    name: "last_name",
    labelKey: "inviteUser.input.lastName.label.text",
    placeholderKey: "inviteUser.input.lastName.placeholder.text",
    required: true,
  },
  {
    name: "email",
    labelKey: "inviteUser.input.email.label.text",
    placeholderKey: "inviteUser.input.email.placeholder.text",
    required: true,
    type: "email",
  },
  // {
  //   name: "password",
  //   labelKey: "inviteUser.input.password.label.text",
  //   placeholderKey: "inviteUser.input.password.placeholder.text",
  //   required: true,
  //   type: "password",
  // },
];

export const getMessageState = (
  userInfo?: any,
  theme?: any,
  t?: any,
  currentPlan?: any
) => {
  let message = "";
  let icon = VDBIcons.WARNING;
  let color = theme["error-01"];
  let backgroundColor = theme["error-02"];
  let type = messageType.Error;
  const isPaymentPending =
    (userInfo.user.plan_details?.upcoming_subscription?.is_upcoming &&
      !userInfo.user.plan_details?.upcoming_subscription?.is_paid) ||
    userInfo.user.plan_details?.upcoming_subscription?.is_payment_pending ||
    userInfo?.user.plan_details?.plan?.is_payment_pending;
  const shouldShowPendingPayment =
    currentPlan?.plan_type === "saas_plan" || userInfo.isEnrolledTrial()
      ? false
      : !userInfo.isPaidPlan();

  if (shouldShowPendingPayment && isPaymentPending) {
    message = t(
      "account.overview.card.paymentPending.message.text",
      `<div>Your payment is pending. Need help? Contact <a href="mailto:info@vdbapp.com">info@vdbapp.com</a></div>`
    );
    icon = VDBIcons.WARNING;
    color = theme["error-01"];
    backgroundColor = theme["error-02"];
    type = messageType.Error;
  } else if (
    !userInfo.user.plan_details?.plan?.is_active &&
    userInfo.user.plan_details?.plan?.is_trial &&
    new Date(userInfo.user.plan_details?.plan?.expiry) < new Date()
  ) {
    message = t(
      "account.overview.card.trialEnded.message.text",
      `<div>Your Trial has ended. Please purchase a plan to continue with full access. Need help? Contact  <a href="mailto:info@vdbapp.com">info@vdbapp.com</a></div>`
    );
    icon = VDBIcons.WARNING;
    color = theme["error-01"];
    backgroundColor = theme["error-02"];
    type = messageType.Error;
  } else if (
    userInfo.user.plan_details?.upcoming_subscription?.is_upcoming &&
    userInfo.user.plan_details?.upcoming_subscription?.is_paid &&
    !userInfo.user.plan_details?.upcoming_subscription?.is_active
  ) {
    message = t(
      "account.overview.card.planConfirmed.message.text",
      "Your paid plan will begin automatically once your trial ends."
    );
    icon = VDBIcons.CHECKMARK;
    color = theme["success-01"];
    backgroundColor = theme["success-02"];
    type = messageType.Success;
  }
  return { message, icon, color, type, backgroundColor };
};
